import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { showError, showSuccess } from "../lib/toast";
import { authApi } from "../lib/authApi";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle,
  GraduationCap,
  Sparkles,
  AlertCircle,
  Shield,
  ChevronRight,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type Role = "student";

interface FormState {
  username: string;
  email: string;
  password: string;
  role: Role;
  firstName: string;
  lastName: string;
}

interface FieldError {
  username?: string;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  general?: string;
}

// ─── Password Strength ────────────────────────────────────────────────────────

const getStrength = (pw: string) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};

const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
const strengthColor = [
  "",
  "bg-red-400",
  "bg-orange-400",
  "bg-amber-400",
  "bg-emerald-400",
];
const strengthText = [
  "",
  "text-red-500",
  "text-orange-500",
  "text-amber-500",
  "text-emerald-600",
];

// ─── Component ────────────────────────────────────────────────────────────────

const CreateAccountPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState<FormState>({
    username: "",
    email: "",
    password: "",
    role: "student",
    firstName: "",
    lastName: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState<FieldError>({});
  const [loading, setLoading] = useState(false);

  const strength = getStrength(form.password);

  const validate = (): boolean => {
    const e: FieldError = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.username.trim()) e.username = "Username is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.password) e.password = "Password is required";
    else if (form.password.length < 8)
      e.password = "Must be at least 8 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await register({
        username: form.username,
        email: form.email,
        password: form.password,
        role: form.role,
        firstName: form.firstName,
        lastName: form.lastName,
      });
      showSuccess("Account created! Please check your email to verify.");
      navigate(`/verify-email?email=${encodeURIComponent(form.email)}`);
    } catch (err: unknown) {
      const error = err as Error;
      setErrors({
        general: error.message || "Registration failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const set =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field as keyof FieldError])
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  return (
    <div className="flex items-center justify-center min-h-screen p-2 bg-gradient-to-br from-amber-50 via-white to-orange-50/30">
      <div className="relative w-full max-w-xs">
        {/* Main Card */}
        <div className="relative overflow-hidden border rounded-lg shadow-lg bg-white/80 backdrop-blur-sm border-white/50">
          {/* Decorative Top Bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500"></div>

          {/* Logo Section */}
          <div className="justify-center px-3 pt-4 pb-1 text-center">
            <div className="flex items-center justify-center gap-0 my-2">
                <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white transition-all pl-2 py-0.5">
                  Learn
                </span>
                <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg pr-2 py-0.5">
                  Hub
                </span>
              </div>
            <h1 className="mt-3 text-base font-bold text-gray-800">
              Create Account
            </h1>
            <p className="text-[12px] text-gray-600 ">
              Join LearnHub to start learning
            </p>
          </div>

          {/* Content */}
          <div className="px-3 pb-4 my-3">
            {/* Header Text */}
            <div className="mb-4">
              <p className="text-[12px] font-bold text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-orange-600 hover:text-orange-700 transition-colors inline-flex items-center gap-0.5"
                >
                  Sign in <ChevronRight className="w-2.5 h-2.5" />
                </Link>
              </p>
            </div>

            {/* General error */}
            {errors.general && (
              <div className="flex items-center gap-1.5 p-1.5 mb-2 border border-red-200 rounded-lg bg-red-50">
                <AlertCircle className="w-3 h-3 text-red-500 shrink-0" />
                <p className="text-[9px] font-medium text-red-600">
                  {errors.general}
                </p>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-3">
              {/* First Name & Last Name */}
              <div className="grid grid-cols-2 gap-1.5 my-2">
                <div>
                  <label className="block mb-0.5 text-[11px] font-semibold text-gray-700">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute w-3 h-3 text-gray-500 -translate-y-1/2 left-2 top-1/2" />
                    <input
                      type="text"
                      placeholder="John"
                      value={form.firstName}
                      onChange={set("firstName")}
                      className={`w-full pl-6 pr-2 py-2.5 text-[13px] text-gray-800 font-medium rounded-lg border bg-white/50 transition-all outline-none
                        focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                        ${errors.firstName ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-0.5 text-[10px] text-red-500">
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block mb-0.5 text-[11px] font-semibold text-gray-700">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute w-3 h-3 text-gray-500 -translate-y-1/2 left-2 top-1/2" />
                    <input
                      type="text"
                      placeholder="Doe"
                      value={form.lastName}
                      onChange={set("lastName")}
                      className={`w-full pl-6 pr-2 py-2.5 text-[13px] text-gray-800 font-medium rounded-lg border bg-white/50 transition-all outline-none
                        focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                        ${errors.lastName ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-0.5 text-[10px] text-red-500">
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Username */}
              <div>
                <label className="block mb-0.5 text-[11px] font-semibold text-gray-700">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute w-3 h-3 text-gray-500 -translate-y-1/2 left-2 top-1/2" />
                  <input
                    type="text"
                    placeholder="johndoe"
                    value={form.username}
                    onChange={set("username")}
                    className={`w-full pl-6 pr-2 py-2.5 text-[13px] text-gray-800 font-medium rounded-lg border bg-white/50 transition-all outline-none
                      focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                      ${errors.username ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                </div>
                {errors.username && (
                  <p className="mt-0.5 text-[10px] text-red-500">
                    {errors.username}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block mb-0.5 text-[11px] font-semibold text-gray-700">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute w-3 h-3 text-gray-500 -translate-y-1/2 left-2 top-1/2" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={set("email")}
                    className={`w-full pl-6 pr-2 py-2.5 text-[13px] text-gray-800 font-medium rounded-lg border bg-white/50 transition-all outline-none
                      focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                      ${errors.email ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-0.5 text-[10px] text-red-500">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block mb-0.5 text-[11px] font-semibold text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute w-3 h-3 text-gray-500 -translate-y-1/2 left-2 top-1/2" />
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="Min 8 characters"
                    value={form.password}
                    onChange={set("password")}
                    className={`w-full pl-6 pr-6 py-2.5 text-[13px] text-gray-800 font-medium rounded-lg border bg-white/50 transition-all outline-none
                      focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                      ${errors.password ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((p) => !p)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPw ? (
                      <EyeOff className="w-3 h-3" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                  </button>
                </div>

                {/* Password Strength */}
                {form.password.length > 0 && (
                  <div className="mt-1">
                    <div className="flex gap-0.5 mb-0.5">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-0.5 flex-1 rounded-full transition-all duration-300 ${
                            i <= strength
                              ? strengthColor[strength]
                              : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p
                      className={`text-[8px] font-medium ${strengthText[strength]}`}
                    >
                      {strengthLabel[strength]} password
                    </p>
                  </div>
                )}
                {errors.password && (
                  <p className="mt-0.5 text-[8px] text-red-500">
                    {errors.password}
                  </p>
                )}
              </div>
            </div>

            {/* Terms */}
            <p className="text-[11px] text-gray-500 leading-relaxed my-2">
              By creating an account you agree to our{" "}
              <a
                href="/terms"
                className="font-medium text-orange-600 hover:underline"
              >
                Terms
              </a>{" "}
              &{" "}
              <a
                href="/privacy"
                className="font-medium text-orange-600 hover:underline"
              >
                Privacy
              </a>
              .
            </p>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 mb-2 text-[13px] font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-50 group"
            >
              {loading ? (
                <>
                  <svg
                    className="w-3 h-3 text-white animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-[9px]">
                <span className="px-2 text-gray-500 bg-white/80">
                  or continue with
                </span>
              </div>
            </div>

            {/* Google Sign Up */}
            {authApi.getGoogleClientId && authApi.getGoogleClientId() && (
              <button
                type="button"
                onClick={() => authApi.loginWithGoogle()}
                className="flex items-center justify-center w-full gap-1.5 py-2.5 text-[13px] font-medium text-gray-700 transition-all duration-200 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            )}
          </div>
        </div>

        {/* Security Footer */}
        <div className="mt-2 text-center">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/60 backdrop-blur-sm rounded-full">
            <Shield className="w-2 h-2 text-emerald-500" />
            <span className="text-[11px] text-gray-500">
              Secure signup • 2FA available
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.25s ease-out; }
      `}</style>
    </div>
  );
};

export default CreateAccountPage;
